import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTenant } from "@/lib/tenant-context";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { CreditCard, ArrowRight, LogIn, LogOut, Mail, CheckCircle2, Users, Sparkles, Check, User } from "lucide-react";
import { waitlistService } from "@/services";
import cardExample1 from "@/assets/card-example-1.jpg";
import cardExample2 from "@/assets/card-example-2.jpg";
import cardExample3 from "@/assets/card-example-3.jpg";

const CARD_EXAMPLES = [
  { src: cardExample1 },
  { src: cardExample2 },
  { src: cardExample3 },
];

const WaitlistLanding = () => {
  const { tenant_slug } = useParams<{ tenant_slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant, loading } = useTenant();
  const { user, signOut, signInWithMagicLink } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginSent, setLoginSent] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const WAITLIST_OFFSET = 122671;
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [waitlistGoal, setWaitlistGoal] = useState(5000);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);

  // Splash screen state
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!tenant) return;
    const fetchStats = async () => {
      const { count } = await waitlistService.getWaitlistCount(tenant.id);
      if (count !== null) setWaitlistCount(count);

      const { data } = await waitlistService.getWaitlistTenantInfo(tenant.id);
      if (data?.waitlist_goal) setWaitlistGoal(data.waitlist_goal);

      if (user) {
        const { data: entry } = await waitlistService.getUserWaitlistEntry(tenant.id, user.id);
        if (entry) setIsOnWaitlist(true);
      }
    };
    fetchStats();
  }, [tenant, user]);

  const handleMagicLink = async () => {
    setLoginError(null);
    setLoginLoading(true);
    const { error } = await signInWithMagicLink(loginEmail);
    setLoginLoading(false);
    if (error) {
      setLoginError(error);
    } else {
      setLoginSent(true);
    }
  };

  // ─── Splash Screen ───
  if (showSplash || loading) {
    const color = tenant?.primaryColor || "250 85% 60%";
    const name = tenant?.brandName || tenant?.name || "";
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6"
        style={{ background: `linear-gradient(135deg, hsl(${color}), hsl(${color} / 0.75))` }}
      >
        {/* Animated logo */}
        <div className="animate-[scale-in_0.6s_ease-out] flex h-24 w-24 items-center justify-center rounded-3xl bg-white/20 backdrop-blur-sm shadow-2xl">
          <CreditCard className="h-12 w-12 text-white animate-[fade-in_0.8s_ease-out_0.3s_both]" />
        </div>
        {name && (
          <h1 className="text-2xl font-bold font-display text-white tracking-tight animate-[fade-in_0.8s_ease-out_0.5s_both]">
            {name}
          </h1>
        )}
        {/* Pulse dots */}
        <div className="flex gap-2 animate-[fade-in_0.8s_ease-out_0.7s_both]">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-2 w-2 rounded-full bg-white/60"
              style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
        <CreditCard className="h-16 w-16 text-muted-foreground" />
        <h1 className="text-2xl font-bold font-display">Not Found</h1>
        <p className="text-muted-foreground">The link you followed doesn't match any card project.</p>
      </div>
    );
  }

  const progressPercent = Math.min((waitlistCount / waitlistGoal) * 100, 100);
  const lp = tenant.landingPage;
  const merge = (str: string) => str.replace(/\{\{bankName\}\}/g, tenant.brandName);

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ "--tenant-primary": tenant.primaryColor } as React.CSSProperties}
    >
      {/* Top bar */}
      {user && (
        <div className="flex justify-end items-center gap-2 px-4 pt-3">
          {isOnWaitlist && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/t/${tenant_slug}/account`)}>
              <User className="h-4 w-4" /> My Account
            </Button>
          )}
          <Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5" onClick={signOut}>
            <LogOut className="h-4 w-4" /> Sign Out
          </Button>
        </div>
      )}

      {/* Hero */}
      <div className="relative flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            background: `radial-gradient(ellipse at center, hsl(${tenant.primaryColor}), transparent 70%)`,
          }}
        />

        <div className="relative z-10 flex flex-col items-center gap-8 animate-fade-in max-w-2xl">
          {/* Logo */}
          <div
            className="flex h-20 w-20 items-center justify-center rounded-2xl shadow-elevated"
            style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
          >
            <CreditCard className="h-10 w-10 text-white" />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-widest" style={{ color: `hsl(${tenant.primaryColor})` }}>
              {tenant.brandName}
            </p>
            <h1 className="text-4xl font-bold font-display tracking-tight">
              Create Your Custom<br />Debit Card
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              Design a one-of-a-kind AI-powered avatar card that's uniquely yours. Join the waitlist and be first to get it.
            </p>
          </div>

          {/* Primary CTA — above the fold */}
          <Button
            size="lg"
            className="gradient-primary text-primary-foreground font-semibold gap-2 shadow-elevated hover:shadow-card transition-all px-10 py-6 text-lg rounded-2xl"
            onClick={() => {
              if (user && isOnWaitlist) {
                navigate(`/t/${tenant_slug}/account`);
              } else {
                const ref = searchParams.get("ref");
                navigate(`/t/${tenant_slug}/consent${ref ? `?ref=${ref}` : ""}`);
              }
            }}
          >
            {user && isOnWaitlist ? "View My Account" : "Design Your Card"}
            <ArrowRight className="h-5 w-5" />
          </Button>

          {!user && (
            <button
              onClick={() => { setLoginOpen(true); setLoginSent(false); setLoginEmail(""); setLoginError(null); }}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors -mt-4"
            >
              <LogIn className="h-3.5 w-3.5" />
              Already on the list? Sign in
            </button>
          )}

          {/* Card examples */}
          <div className="flex gap-4 items-end justify-center max-w-2xl w-full px-2">
            {CARD_EXAMPLES.map((card, i) => (
              <div
                key={i}
                className="group flex-1"
                style={{ transform: i === 1 ? "translateY(-8px)" : "" }}
              >
                <div className="overflow-hidden rounded-xl shadow-elevated transition-transform group-hover:scale-105 group-hover:-rotate-1">
                  <img src={card.src} alt="Example card" className="w-full h-auto object-cover" />
                </div>
              </div>
            ))}
          </div>

          {/* How it works */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-lg">
            {[
              { icon: Sparkles, title: "Design Your Card", desc: "Choose a style and create your AI avatar" },
              { icon: Users, title: "Join the Waitlist", desc: "Reserve your spot — no charge until we hit our goal" },
              { icon: CreditCard, title: "Get Your Card", desc: "Prepaid debit card delivered to your door" },
            ].map((step) => (
              <div key={step.title} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-muted/50">
                <step.icon className="h-6 w-6" style={{ color: `hsl(${tenant.primaryColor})` }} />
                <h3 className="text-sm font-semibold">{step.title}</h3>
                <p className="text-xs text-muted-foreground text-center">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Waitlist Progress */}
          <div className="w-full max-w-md space-y-3 bg-card rounded-2xl p-6 shadow-card border">
            <div className="flex items-center justify-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4" style={{ color: `hsl(${tenant.primaryColor})` }} />
              <span className="font-display text-lg font-bold">{(waitlistCount + WAITLIST_OFFSET).toLocaleString()}</span>
              <span className="text-muted-foreground">of {(waitlistGoal + WAITLIST_OFFSET).toLocaleString()} people joined</span>
            </div>
            <Progress
              value={progressPercent}
              className="h-3 rounded-full"
            />
            <p className="text-xs text-muted-foreground text-center">
              {waitlistGoal - waitlistCount > 0
                ? `${(waitlistGoal - waitlistCount).toLocaleString()} more people needed to unlock card production!`
                : "🎉 Goal reached! Cards are being produced!"}
            </p>
          </div>

          {/* Secondary CTA */}
          <Button
            size="lg"
            className="gradient-primary text-primary-foreground font-semibold gap-2 shadow-elevated hover:shadow-card transition-all px-8"
            onClick={() => {
              if (user && isOnWaitlist) {
                navigate(`/t/${tenant_slug}/account`);
              } else {
                const ref = searchParams.get("ref");
                navigate(`/t/${tenant_slug}/consent${ref ? `?ref=${ref}` : ""}`);
              }
            }}
          >
            {user && isOnWaitlist ? "View My Status & Referral Code" : "Design Your Card & Join Waitlist"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Pricing Section */}
      {tenant.consumerPricing && tenant.consumerPricing.length > 0 && (
        <div className="border-t bg-muted/30 px-6 py-16">
          <div className="mx-auto max-w-4xl space-y-8">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold font-display tracking-tight">Simple, Transparent Pricing</h2>
              <p className="text-muted-foreground">No hidden fees. Pay only for what you need.</p>
            </div>

            <div className={`grid gap-6 ${tenant.consumerPricing.length === 1 ? "max-w-sm mx-auto" : tenant.consumerPricing.length === 2 ? "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
              {tenant.consumerPricing.map((tier) => (
                <div
                  key={tier.id}
                  className={`relative flex flex-col rounded-2xl border bg-card p-6 shadow-card transition-transform hover:scale-[1.02] ${
                    tier.isHighlighted ? "ring-2 shadow-elevated" : ""
                  }`}
                  style={tier.isHighlighted ? { borderColor: `hsl(${tenant.primaryColor})`, boxShadow: `0 0 0 2px hsl(${tenant.primaryColor} / 0.3)` } : undefined}
                >
                  {tier.isHighlighted && (
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-0.5 text-xs font-semibold text-white"
                      style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
                    >
                      Most Popular
                    </div>
                  )}
                  <div className="space-y-1 mb-4">
                    <h3 className="text-lg font-bold font-display">{tier.name}</h3>
                    <p className="text-sm text-muted-foreground">{tier.description}</p>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-bold font-display">{tier.priceLabel}</span>
                    {tier.period && <span className="text-sm text-muted-foreground ml-1">{tier.period}</span>}
                  </div>
                  <ul className="space-y-2 mb-6 flex-1">
                    {tier.features.filter(Boolean).map((feat, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: `hsl(${tenant.primaryColor})` }} />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full font-semibold ${tier.isHighlighted ? "gradient-primary text-primary-foreground shadow-elevated" : ""}`}
                    variant={tier.isHighlighted ? "default" : "outline"}
                    onClick={() => navigate(`/t/${tenant_slug}/consent`)}
                  >
                    {tier.ctaLabel}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Magic link dialog */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Sign in with email
            </DialogTitle>
            <DialogDescription>
              Enter your email and we'll send you a magic link to sign in.
            </DialogDescription>
          </DialogHeader>

          {loginSent ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="font-medium">Check your inbox!</p>
              <p className="text-sm text-muted-foreground">
                We sent a sign-in link to <strong>{loginEmail}</strong>.
              </p>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleMagicLink(); }} className="space-y-4">
              <Input
                type="email"
                placeholder="you@example.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
                autoFocus
              />
              {loginError && <p className="text-sm text-destructive">{loginError}</p>}
              <Button type="submit" className="w-full" disabled={loginLoading || !loginEmail}>
                {loginLoading ? "Sending…" : "Send magic link"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t py-4 text-center text-xs text-muted-foreground space-y-2">
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => navigate(`/t/${tenant_slug}/privacy`)} className="underline hover:text-foreground transition-colors">Privacy Policy</button>
          <span>·</span>
          <button onClick={() => navigate(`/t/${tenant_slug}/terms`)} className="underline hover:text-foreground transition-colors">Terms & Conditions</button>
        </div>
        <p>{merge(lp.footerText)} · {tenant.brandName}</p>
      </footer>
    </div>
  );
};

export default WaitlistLanding;
